import { Component, computed, inject, ViewChild, OnInit } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';

import { AuthService } from '../../../../Shared/Services/auth.service';

import { UserFormFacade } from '../../../../../Facades/user-form.facade';
import { ListRolesFacade } from '../../../../../Facades/list-roles.facade';
import { IntuneUser } from '../../../../../Models/Intune/intune-user.model';
import { COMMON_IMPORTS } from '../../../../Shared/common-imports';
import { AvContentContainerComponent } from '../../../../Shared/Components/av-content-container/av-content-container.component';
import { UserCreateStep1Component } from '../../Partials/user-create-step-1/user-create-step-1.component';
import { UserCreateStep2Component } from '../../Partials/user-create-step-2/user-create-step-2.component';

@Component({
  selector: 'app-user-create',
  imports: [COMMON_IMPORTS, AvContentContainerComponent, UserCreateStep1Component, UserCreateStep2Component],
  providers: [UserFormFacade, ListRolesFacade],
  templateUrl: './user-create.component.html',
  styleUrls: ['./user-create.component.scss'],
})
export class UserCreateComponent {
  private readonly _formBuilder = inject(FormBuilder);
  private readonly _router = inject(Router);
  private readonly _authService = inject(AuthService);
  readonly facade = inject(UserFormFacade);

  @ViewChild(UserCreateStep2Component) private _step2!: UserCreateStep2Component;

  readonly currentUser = toSignal(this._authService.currentUser$, { initialValue: null });

  readonly rbacOff = computed(() => this.currentUser()?.hasEntitlement('rbac') === false);

  public userFormGroup!: FormGroup;
  public roleFormGroup!: FormGroup;

  constructor() {
    this.initForms();
  }

  private initForms(): void {
    this.userFormGroup = this._formBuilder.group({
      userSearch: [null as IntuneUser | string | null, Validators.required]
    });

    if (this.rbacOff()) {
      return;
    }

    this.roleFormGroup = this._formBuilder.group({
      roleId: ['', Validators.required]
    });
  }

  onCancel(): void {
    this.userFormGroup.reset();
    this.facade.setSelectedUser(null);
    this.facade.setSearchTerm('');

    if (this.rbacOff()) {
      return;
    }

    this.roleFormGroup.reset();
    this._step2.reset();
  }

  async onSubmit(): Promise<void> {
    if (this.rbacOff()) {
      await this.submitSingleStep();

      return;
    }

    if (this.userFormGroup.valid && this.roleFormGroup.valid) {
      const scopes = this._step2.getScopes();

      const payload = {
        azure_user_id: this.facade.selectedUser()!.id!,
        role_id: this.roleFormGroup.value.roleId as string,
        ...(Object.keys(scopes).length > 0 ? { scopes } : {}),
      };

      await this.facade.createUser(payload);

      this.userFormGroup.reset();
      this.roleFormGroup.reset();
      this.facade.setSelectedUser(null);
      this.facade.setSearchTerm('');
      this._step2.reset();

      this._router.navigate(['/settings/users']);
    }
  }

  private async submitSingleStep(): Promise<void> {
    if (!this.userFormGroup.valid || !this.facade.selectedUser()) {
      return;
    }

    await this.facade.createUser({ azure_user_id: this.facade.selectedUser()!.id! });

    this.userFormGroup.reset();
    this.facade.setSelectedUser(null);
    this.facade.setSearchTerm('');

    this._router.navigate(['/settings/users']);
  }
}